if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        if (!productInfoNode) return null; // <-- guard
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          // Preserve selected options (Size and Width only, not Color)
          const preservedSelections = this.preserveSelectedOptions();
          
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks, [
            ...(this.postProcessHtmlCallbacks || []),
            // Restore selected options after update (silently, no events)
            (newNode) => {
              this.restoreSelectedOptionsSilently(newNode, preservedSelections);
            }
          ]);
        }
      }
      
      preserveSelectedOptions() {
        const selections = {};
        const variantSelects = this.variantSelectors;
        if (!variantSelects) return selections;
        
        // Preserve unisex gender toggle (Men/Women) so it stays when size is selected
        const activeGenderBtn = variantSelects.querySelector('.js-unisex-gender-toggle.g-active');
        if (activeGenderBtn) {
          selections.gender = activeGenderBtn.dataset.genderLabel || activeGenderBtn.textContent.trim() || '';
        }
        
        // Only preserve Size and Width, not Color (Color will be selected by default)
        // Get all selected radio inputs (for button type options like Size/Width)
        const radioInputs = variantSelects.querySelectorAll('fieldset.product-form__input--pill input[type="radio"]:checked');
        radioInputs.forEach(input => {
          const fieldset = input.closest('fieldset');
          if (fieldset) {
            const h2Label = fieldset.querySelector('h2.select_gender');
            if (h2Label) {
              const labelText = h2Label.textContent.toLowerCase();
              if (labelText.includes('size')) {
                selections.size = input.value;
              } else if (labelText.includes('width')) {
                selections.width = input.value;
              }
            }
          }
        });
        
        // Get all selected dropdown options
        const selects = variantSelects.querySelectorAll('select.select__select');
        selects.forEach(select => {
          const label = select.closest('.product-form__input--dropdown')?.querySelector('.form__label');
          if (label && select.value) {
            const labelText = label.textContent.toLowerCase();
            if (labelText.includes('size')) {
              selections.size = select.value;
            } else if (labelText.includes('width')) {
              selections.width = select.value;
            }
          }
        });
        
        return selections;
      }
      
      restoreSelectedOptionsSilently(newVariantSelects, selections) {
        if (!newVariantSelects || !selections) return;
        
        // Restore unisex gender toggle (Men/Women) so selected tab stays
        if (selections.gender) {
          const genderButtons = newVariantSelects.querySelectorAll('.js-unisex-gender-toggle');
          genderButtons.forEach((btn) => {
            const btnLabel = btn.dataset.genderLabel || btn.textContent.trim() || '';
            const isMatch = btnLabel.toLowerCase() === selections.gender.toLowerCase();
            btn.classList.toggle('g-active', isMatch);
            btn.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
          });
          const unisexFilter = newVariantSelects.querySelector('unisex-size-filter');
          if (unisexFilter && typeof unisexFilter.applyFilter === 'function') {
            unisexFilter.applyFilter(selections.gender);
          }
        }
        
        // Restore button type options (Size/Width) - silently, no change events
        const buttonFieldsets = newVariantSelects.querySelectorAll('fieldset.product-form__input--pill');
        buttonFieldsets.forEach(fieldset => {
          const h2Label = fieldset.querySelector('h2.select_gender');
          if (h2Label) {
            const labelText = h2Label.textContent.toLowerCase();
            let optionKey = null;
            if (labelText.includes('size')) {
              optionKey = 'size';
            } else if (labelText.includes('width')) {
              optionKey = 'width';
            }
            
            if (optionKey && selections[optionKey]) {
              const input = fieldset.querySelector(`input[type="radio"][value="${selections[optionKey]}"]`);
              if (input && !input.disabled && !input.classList.contains('disabled')) {
                // Uncheck all other inputs in this fieldset first
                fieldset.querySelectorAll('input[type="radio"]').forEach(radio => {
                  radio.checked = false;
                });
                // Check the selected input (silently, no event)
                input.checked = true;
                // Update the label visual state
                const label = fieldset.querySelector(`label[for="${input.id}"]`);
                if (label) {
                  fieldset.querySelectorAll('label').forEach(l => l.classList.remove('active'));
                  label.classList.add('active');
                }
              }
            }
          }
        });
        
        // Restore dropdown options (silently, no change events)
        const selects = newVariantSelects.querySelectorAll('select.select__select');
        selects.forEach(select => {
          const label = select.closest('.product-form__input--dropdown')?.querySelector('.form__label');
          if (label) {
            const labelText = label.textContent.toLowerCase();
            let optionKey = null;
            if (labelText.includes('size')) {
              optionKey = 'size';
            } else if (labelText.includes('width')) {
              optionKey = 'width';
            }
            
            if (optionKey && selections[optionKey]) {
              const option = Array.from(select.options).find(opt => opt.value === selections[optionKey]);
              if (option && !option.disabled) {
                select.value = selections[optionKey];
                // No change event dispatched - just visual update
              }
            }
          }
        });
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}


if (!customElements.get('unisex-size-filter')) {
  class UnisexSizeFilter extends HTMLElement {
    connectedCallback() {
      if (this.closest('quick-add-modal')) return;
      try {
        this.sectionId = this.dataset.sectionId || this.getAttribute('data-section-id');
        this.sizeOptionName =
          (this.dataset.sizeOptionName || this.getAttribute('data-size-option-name') || 'Size')
            .toString()
            .toLowerCase();

        const configScript = this.querySelector('.unisex-size-filter__config');
        if (!configScript) return;

        let rawConfig;
        try {
          rawConfig = JSON.parse(configScript.textContent || '[]');
        } catch (e) {
          console.error('UnisexSizeFilter: invalid config JSON', e);
          return;
        }

        this.sizeMap = this.buildSizeMap(rawConfig);
        if (!this.sizeMap) return;

        // Find the nearest variant-selects container
        const productInfo = this.closest('product-info');
        this.variantSelects = productInfo?.variantSelectors || this.closest('variant-selects') || null;
        if (!this.variantSelects) return;

        // Gender toggle buttons for unisex products
        this.genderButtons = Array.from(
          this.variantSelects.querySelectorAll('.js-unisex-gender-toggle')
        );
        if (!this.genderButtons.length) return;

        this.onGenderClick = this.onGenderClick.bind(this);
        this.genderButtons.forEach((btn) => {
          btn.addEventListener('click', this.onGenderClick);
        });

        // Initial gender = first button label
        const initialGender =
          this.genderButtons[0].dataset.genderLabel ||
          this.genderButtons[0].textContent.trim();
        this.currentGender = initialGender;

        this.applyFilter(initialGender);
      } catch (e) {
        console.error('UnisexSizeFilter: initialization error', e);
      }
    }

    disconnectedCallback() {
      if (this.genderButtons) {
        this.genderButtons.forEach((btn) => {
          btn.removeEventListener('click', this.onGenderClick);
        });
      }
    }

    buildSizeMap(config) {
      const entries = Array.isArray(config) ? config : [config];
      const map = {};

      entries.forEach((entry) => {
        if (!entry) return;
        const rawGender = entry.gender || entry.Gender;
        const rawSizes = entry.size || entry.Size || entry.sizes || entry.Sizes;
        if (!rawGender || !rawSizes) return;

        const genderKey = String(rawGender).toLowerCase().trim();
        const sizes = String(rawSizes)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);

        if (!sizes.length) return;
        map[genderKey] = sizes;
      });

      return Object.keys(map).length ? map : null;
    }

    onGenderClick(event) {
      const btn = event.currentTarget;
      const genderLabel = btn.dataset.genderLabel || btn.textContent.trim();
      this.currentGender = genderLabel;

      // Update pressed / active state
      this.genderButtons.forEach((button) => {
        const isActive = button === btn;
        button.classList.toggle('g-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.applyFilter(genderLabel);
    }

    applyFilter(genderLabel) {
      if (!this.variantSelects || !this.sizeMap) return;

      const key = String(genderLabel).toLowerCase().trim();
      const allowedSizes = this.sizeMap[key];
      if (!allowedSizes || !allowedSizes.length) return;

      // Radio button (pill) size options
      const radioInputs = Array.from(
        this.variantSelects.querySelectorAll(
          'input[type="radio"][data-option-name][data-option-display-value]'
        )
      ).filter((input) => {
        const name = (input.dataset.optionName || '').toLowerCase();
        return name.includes('size');
      });

      radioInputs.forEach((input) => {
        const sizeValue = input.dataset.optionDisplayValue || input.value;
        const isAllowed = allowedSizes.includes(sizeValue);
        const label = this.variantSelects.querySelector(`label[for="${input.id}"]`);

        input.disabled = !isAllowed;
        input.classList.toggle('unisex-size-hidden', !isAllowed);
        if (label) {
          label.classList.toggle('unisex-size-hidden', !isAllowed);
        }
      });

      // Dropdown size options
      const selects = Array.from(
        this.variantSelects.querySelectorAll('select.select__select')
      );

      selects.forEach((select) => {
        const labelEl = select
          .closest('.product-form__input--dropdown')
          ?.querySelector('.form__label');
        if (!labelEl) return;

        const labelText = labelEl.textContent.toLowerCase();
        if (!labelText.includes('size')) return;

        Array.from(select.options).forEach((option) => {
          const value = option.value;
          if (!value) return;

          const isAllowed = allowedSizes.includes(value);
          option.disabled = !isAllowed;
          option.hidden = !isAllowed;
        });
      });
    }
  }

  customElements.define('unisex-size-filter', UnisexSizeFilter);
}


document.addEventListener('click', (event) => {
  const btn = event.target.closest('.variant-button');
  if (!btn) return; // only act when a gender button is clicked

  const productUrl = btn.getAttribute('product-url');
  const productInfo = btn.closest('product-info');
  if (!productUrl || !productInfo) return;
  if (productInfo.closest('quick-add-modal')) return;

  // optional: show loading state
  btn.classList.add('loading');

  productInfo.renderProductInfo({
    requestUrl: productUrl,
    targetId: btn.id || 'gender-switch',
    callback: productInfo.handleSwapProduct(productUrl, false), // only swap inside product-info
  });

  // optional: remove loading after short delay
  setTimeout(() => btn.classList.remove('loading'), 1500);
});

// Ensure unisex gender buttons always reflect the current selection
document.addEventListener('click', (event) => {
  const genderBtn = event.target.closest('.js-unisex-gender-toggle');
  if (!genderBtn) return;

  const container = genderBtn.closest('.gender_button_row') || document;
  const buttons = container.querySelectorAll('.js-unisex-gender-toggle');

  buttons.forEach((btn) => {
    const isActive = btn === genderBtn;
    btn.classList.toggle('g-active', isActive);
    btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });

  // If the size guide popup is already open, update its visible table
  if (window.PahiModal && typeof window.PahiModal.open === 'function') {
    const modalEl = document.querySelector('.pahi-modal');
    if (modalEl && modalEl.classList.contains('pahi-modal--active')) {
      const gender =
        genderBtn.dataset.genderLabel || genderBtn.textContent.trim() || '';
      window.PahiModal.open(gender);
    }
  }
});

// Unisex size guide: when clicking \"Size guide\" on a unisex product,
// show Men/Women table based on selected gender (data-size-guide-table)
document.addEventListener('click', (event) => {
  const sizeGuideBtn = event.target.closest('#size_guide-popup');
  if (!sizeGuideBtn) return;

  // Only override behavior when unisex gender toggles exist on this product
  const root = sizeGuideBtn.closest('product-info') || document;
  const activeGenderBtn = root.querySelector('.js-unisex-gender-toggle.g-active');
  if (!activeGenderBtn) return; // non‑unisex products keep default metafield behavior

  const gender =
    activeGenderBtn.dataset.genderLabel || activeGenderBtn.textContent.trim() || 'Men';

  if (window.PahiModal && typeof window.PahiModal.open === 'function') {
    // Inline onclick=\"PahiModal.open()\" may still fire first and show \"all\",
    // this call will immediately switch the visible table to men/women.
    window.PahiModal.open(gender);
  }
});
